import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import NotFound from "../../../notfound/NotFound";
import { Button, Form, Input, Select, message, Spin } from "antd";

import { LoadingOutlined } from "@ant-design/icons";

import {
  editCommercialDate,
  getAllChannels,
  getCommercialDateById,
} from "../../../../api/Services";
import CommercialDateEditions from "../editions/CommercialDateEditions";
import styles from "./ViewCommercialDate.module.scss";

const ViewCommercialDate = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [commercialDate, setCommercialDate] = useState<any>();
  const [channelOptions, setChannelOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const [editForm] = Form.useForm();

  const { id } = useParams();

  useEffect(() => {
    handleFetchCommercialDate();
    getAllChannels().then((channels: any[]) => {
      setChannelOptions(
        (channels ?? []).map((c) => ({
          value: c.id,
          label:
            c?.type?.descriptionEs || c?.type?.type || `${t("Common-Channels")} ${c.id}`,
        }))
      );
    });
  }, []);

  const handleFetchCommercialDate = async () => {
    let response = await getCommercialDateById(id!);
    setCommercialDate(response);
    setLoading(false);
  };

  const handleEditCommercialDate = async () => {
    if (!id) return;

    const { name, channelIds } = editForm.getFieldsValue();

    let response = await editCommercialDate(id, name, channelIds);
    if (response) message.success(t("Common-EditSuccessMessage"));
    else message.error(t("Common-EditErrorMessage"));
  };

  const handleCancelButton = () => {
    editForm.resetFields();
  };

  return loading ? (
    <Spin
      indicator={<LoadingOutlined className="ol-loading-spin-icon" />}
      delay={500}
    />
  ) : !commercialDate ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-CommercialDate")}</h1>
        <div className={styles.actionButtonsWrapper}>
          <Button
            onClick={handleEditCommercialDate}
            className={styles.saveButton}
            type="primary"
          >
            {t("Common-Save")}
          </Button>
          <Button
            onClick={handleCancelButton}
            className={styles.cancelButton}
            type="primary"
          >
            {t("Common-Cancel")}
          </Button>
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <div className={styles.infoWrapper}>
          <Form
            name="editCommercialDate"
            form={editForm}
            layout="vertical"
            autoComplete="off"
            className={styles.newForm}
            preserve={false}
          >
            <Form.Item
              initialValue={commercialDate?.name}
              label={t("Common-Name")}
              name="name"
              className={styles.editInfoItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Name"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} />
            </Form.Item>
            <Form.Item
              initialValue={commercialDate?.channels?.map((c: any) => c.id)}
              label={t("Common-Channels")}
              name="channelIds"
              className={styles.editInfoItem}
              tooltip={t("CommercialDate-ChannelsHint")}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder={t("CommercialDate-ChannelsPlaceholder")}
                options={channelOptions}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
              />
            </Form.Item>
          </Form>
        </div>

        <CommercialDateEditions commercialDateId={Number(id)} />
      </div>
    </div>
  );
};

export default ViewCommercialDate;
